import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { Label } from './Label';

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: ReactNode;
  containerClassName?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ label, hint, className, containerClassName, id, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    const fieldId = id ?? props.name;

    return (
      <div className={cn('space-y-2', containerClassName)}>
        {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
        <div className="relative">
          <input
            ref={ref}
            id={fieldId}
            type={visible ? 'text' : 'password'}
            className={cn(
              'h-10 w-full rounded-xl border border-[#3D3328]/15 bg-white pl-3 pr-10 text-sm text-[#1A1A1A] placeholder:text-[#8A7A6D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A1A]/20 focus-visible:border-[#1A1A1A] disabled:cursor-not-allowed disabled:opacity-60',
              className,
            )}
            {...props}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#78665A] hover:text-[#1A1A1A] hover:bg-[#FAF8F5]"
            aria-label={visible ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {hint ? <p className="text-xs text-[#78665A]">{hint}</p> : null}
      </div>
    );
  },
);
PasswordField.displayName = 'PasswordField';
